import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import Wordmark from "@/components/Wordmark";
import ThemeToggle from "@/components/ThemeToggle";
import { useAuth } from "@/lib/auth";

const NAV_LINKS = [
  { href: "#hero",         label: "Try Rammy",       testid: "nav-try" },
  { href: "#hero-features",label: "Features",       testid: "nav-features" },
  { href: "#why-not-chatgpt", label: "Why Rammy",   testid: "nav-why" },
  { href: "#voice",        label: "Voice",          testid: "nav-voice" },
  { href: "#memory",       label: "Memory",         testid: "nav-memory" },
  { href: "#pricing",      label: "Pricing",        testid: "nav-pricing" },
];

export function Nav({ authed }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Glassmorph nav — start transparent, add subtle backdrop after 8px scroll.
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  // Lock body scroll while the mobile drawer is open — otherwise the drawer
  // itself scrolls the page underneath, which reads as broken on iOS Safari.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-40 transition-[background-color,border-color,box-shadow] duration-300 ${
          scrolled
            ? "backdrop-blur-xl bg-[var(--rm-bg)]/85 border-b border-[var(--rm-border)] shadow-[0_1px_20px_rgba(17,19,24,0.03)]"
            : "backdrop-blur-md bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-10 h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <Wordmark testId="nav-wordmark" />
          </Link>

          {/* Desktop nav — hidden on mobile; the hamburger below owns the small-screen story. */}
          <div className="hidden md:flex items-center gap-0.5 text-[13px] font-medium">
            {NAV_LINKS.map(l => (
              <a
                key={l.testid}
                href={l.href}
                data-testid={l.testid}
                className="px-3 py-2 rounded-full text-[var(--rammy-text-sec)] hover:text-[var(--rammy-text)] hover:bg-[var(--rm-bg-subtle)] transition-colors"
              >
                {l.label}
              </a>
            ))}
            {isAdmin && (
              <Link to="/admin" data-testid="nav-admin" className="px-3 py-2 rounded-full text-[var(--rammy-text-sec)] hover:text-[var(--rammy-text)] hover:bg-[var(--rm-bg-subtle)] transition-colors">
                Dashboard
              </Link>
            )}
          </div>

          <div className="flex items-center gap-2">
            <ThemeToggle testId="nav-theme-toggle" className="hidden sm:inline-flex" />
            {authed ? (
              <Link to="/app" data-testid="nav-open-app">
                <Button className="btn-pill btn-primary h-10 px-5">Open Rammy</Button>
              </Link>
            ) : (
              <>
                {/* Sign in — visible from `sm` up. Below that, it lives in the mobile drawer. */}
                <Link to="/login" data-testid="nav-login" className="hidden sm:inline">
                  <Button variant="ghost" className="btn-pill h-10 px-4 text-[var(--rammy-text-sec)] hover:text-[var(--rammy-text)]">Sign in</Button>
                </Link>
                <Link to="/signup" data-testid="nav-signup">
                  <Button className="btn-pill btn-primary h-10 px-3.5 sm:px-5 text-[13px] sm:text-sm whitespace-nowrap">Try Rammy free</Button>
                </Link>
              </>
            )}
            {/* Hamburger — mobile only. Sits AFTER the primary CTA so the CTA
                stays the most prominent action; the burger is a secondary
                affordance for section navigation + sign in. */}
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full text-[var(--rammy-text)] hover:bg-[var(--rm-bg-subtle)] transition-colors"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              data-testid="nav-menu-toggle"
            >
              <Menu size={20} strokeWidth={1.8} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile drawer — full-height slide-in from the right. Focuses the
          user on the six section links + sign in + primary CTA, nothing else.
          Backdrop tap closes; ESC closes; body scroll is locked. */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-50 md:hidden"
          role="dialog"
          aria-modal="true"
          id="mobile-menu"
          data-testid="mobile-menu"
        >
          <div
            className="absolute inset-0 bg-[var(--rm-ink-900)]/50 backdrop-blur-[2px]"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
            data-testid="mobile-menu-backdrop"
          />
          <div
            className="absolute right-0 top-0 bottom-0 w-[86%] max-w-sm bg-[var(--rm-bg)] shadow-[0_20px_60px_rgba(17,19,24,0.25)] flex flex-col animate-[slideInRight_240ms_ease-out]"
            style={{ animation: "slideInRight 240ms ease-out" }}
          >
            {/* Drawer header — mirror the nav for continuity, with a clear close. */}
            <div className="h-16 px-6 flex items-center justify-between border-b border-[var(--rammy-border)]">
              <div className="flex items-center gap-2.5">
                <Wordmark testId="m-nav-wordmark" />
              </div>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="inline-flex items-center justify-center w-10 h-10 rounded-full text-[var(--rammy-text)] hover:bg-[var(--rm-bg-subtle)] transition-colors"
                aria-label="Close menu"
                data-testid="mobile-menu-close"
              >
                <X size={20} strokeWidth={1.8} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-4 py-5">
              <ul className="space-y-1">
                {NAV_LINKS.map(l => (
                  <li key={l.testid}>
                    <a
                      href={l.href}
                      onClick={() => setMenuOpen(false)}
                      data-testid={`m-${l.testid}`}
                      className="block px-3 py-3 rounded-xl text-[16px] font-medium text-[var(--rammy-text)] hover:bg-[var(--rammy-surface-alt)] transition-colors"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
                {isAdmin && (
                  <li>
                    <Link
                      to="/admin"
                      onClick={() => setMenuOpen(false)}
                      data-testid="m-nav-admin"
                      className="block px-3 py-3 rounded-xl text-[16px] font-medium text-[var(--rammy-text)] hover:bg-[var(--rammy-surface-alt)] transition-colors"
                    >
                      Dashboard
                    </Link>
                  </li>
                )}
              </ul>
            </nav>

            {/* Bottom action stack — Sign in + primary CTA. Sign in sits above
                the CTA so the primary action is still the closest to the thumb
                (bottom of screen on mobile). */}
            <div className="border-t border-[var(--rammy-border)] p-4 space-y-2">
              <div className="flex items-center justify-between px-1 pb-1">
                <span className="text-[13px] text-[var(--rm-text-muted)]">Appearance</span>
                <ThemeToggle testId="m-nav-theme-toggle" />
              </div>
              {authed ? (
                <Link
                  to="/app"
                  onClick={() => setMenuOpen(false)}
                  data-testid="m-nav-open-app"
                >
                  <Button className="btn-pill btn-primary w-full h-12">Open Rammy</Button>
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMenuOpen(false)}
                    data-testid="m-nav-login"
                  >
                    <Button
                      variant="ghost"
                      className="btn-pill w-full h-12 border border-[var(--rammy-border)] bg-[var(--rm-surface)] text-[var(--rammy-text)] hover:bg-[var(--rammy-surface-alt)]"
                    >
                      Sign in
                    </Button>
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setMenuOpen(false)}
                    data-testid="m-nav-signup"
                  >
                    <Button className="btn-pill btn-primary w-full h-12">Try Rammy free</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
